import { Formik, Form as FormikForm, FormikHelpers, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputText from "../../../components/inputs/inputText";
import ScrollToFieldError from "../../../components/ScrollToFieldError";
import Form from "react-bootstrap/Form";
import NewEventsImg from "./images/newEvents.png";
import UpcomingEventsImg from "./images/upcomingEvents.png";
import PopularPerformersImg from "./images/popularPerformers.png";
import { validationSchemaGeneralInfo } from "../../../validations/whiteLabel";
import { WhiteLaelStepEnum } from "../../../types";
import {
  isDisabledWhiteLabelProjectTabs,
  whiteLabelJRActive,
  whiteLabelJRStep,
  whiteLabelStep,
  whiteLabelValidJRSteps,
} from "../../../qraphql/globalVar/state";
import ImagePopover from "../../../components/tooltips/imagePopover";
import {
  useCreateOrUpdateWhiteLabelGeneralInformationMutation,
  useOrganizerWhiteLabelQuery,
} from "../../../qraphql/whiteLabel/whiteLabel.hooks";
import ApolloError from "../../../components/apolloError";
import CustomToast from "../../../components/tooltips/CustomToast";
import { isMobile } from "react-device-detect";
import { useReactiveVar } from "@apollo/client";
import { JoyrideHits } from "../../../components/joyride/whiteLabel/JoyrideHits";

interface IGeneralForm {
  projectName: string;
  isNewEvents: boolean;
  isUpcomingEvents: boolean;
  isPopularPerformers: boolean;
}

export default function GeneralInfo() {
  const { t } = useTranslation();
  const formRef = useRef<FormikProps<IGeneralForm>>(null);
  const currentWhiteLabelStep = useReactiveVar(whiteLabelStep);
  const whiteLabelValidJRStepsValue = useReactiveVar(whiteLabelValidJRSteps);
  const whiteLabelJRActiveValue = useReactiveVar(whiteLabelJRActive);

  const [
    createOrUpdateWhiteLabelGeneralInformation,
    {
      loading: createOrUpdateWhiteLabelGeneralInformationLoading,
      error: createOrUpdateWhiteLabelGeneralInformationError,
    },
  ] = useCreateOrUpdateWhiteLabelGeneralInformationMutation();
  const { data: whiteLabelInfo, loading: whiteLabelInfoLoading } =
    useOrganizerWhiteLabelQuery();

  const [isShowToast, setIsShowToast] = useState(false);

  const onSubmit = async (
    values: IGeneralForm,
    { setSubmitting }: FormikHelpers<IGeneralForm>
  ) => {
    await createOrUpdateWhiteLabelGeneralInformation({
      variables: {
        input: {
          projectName: values.projectName,
          isShowNewEvents: values.isNewEvents,
          isShowPopularPerformers: values.isPopularPerformers,
          isShowUpcomingEvents: values.isUpcomingEvents,
        },
      },
      onCompleted: () => {
        setIsShowToast(true);
        if (currentWhiteLabelStep < WhiteLaelStepEnum.CustomDomain) {
          whiteLabelStep(WhiteLaelStepEnum.CustomDomain);
        }
        isDisabledWhiteLabelProjectTabs(false);
        if (whiteLabelJRActiveValue) {
          whiteLabelJRActive(false);
          whiteLabelJRStep(3);
          setTimeout(() => {
            (
              document.querySelector(".seo-settings-tab") as HTMLElement | null
            )?.click();
          }, 500);
        }
      },
    });
    setSubmitting(false);
  };

  const initialValues: IGeneralForm = {
    projectName: "",
    isNewEvents: true,
    isUpcomingEvents: true,
    isPopularPerformers: true,
  };

  useEffect(() => {
    if (whiteLabelInfo) {
      const whiteLabelConfig =
        whiteLabelInfo.organizer.whiteLabel?.whiteLabelConfig;
      formRef.current?.setValues({
        projectName: whiteLabelInfo.organizer.whiteLabel?.projectName || "",
        isNewEvents:
          whiteLabelConfig?.showNewEvents === undefined
            ? true
            : whiteLabelConfig.showNewEvents,
        isUpcomingEvents:
          whiteLabelConfig?.showUpcomingEvents === undefined
            ? true
            : whiteLabelConfig.showUpcomingEvents,
        isPopularPerformers:
          whiteLabelConfig?.showPopularPerformers === undefined
            ? true
            : whiteLabelConfig.showPopularPerformers,
      });
    }
  }, [whiteLabelInfo]);

  if (whiteLabelInfoLoading) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: 200 }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchemaGeneralInfo(t)}
        enableReinitialize
      >
        {({ isSubmitting, isValid, submitCount, values, setFieldValue }) => {
          useEffect(() => {
            if (values.projectName && isValid) {
              whiteLabelValidJRSteps({
                ...whiteLabelValidJRStepsValue,
                step0: true,
              });
            } else {
              whiteLabelValidJRSteps({
                ...whiteLabelValidJRStepsValue,
                step0: false,
              });
            }
          }, [values.projectName, isValid]);
          return (
            <FormikForm className="mb-5 pb-5">
              <ScrollToFieldError />
              <div id="project-name">
                <InputText
                  name="projectName"
                  className="mb-5"
                  placeholderHTML={`${t("whiteLabel.Project Name")} <span class='text-danger'>*</span>`}
                  placeholder={`${t("whiteLabel.Project Name")}`}
                />
              </div>
              <div id="main-page-blocks">
                <div className="fs-5 fw-bold mb-3">
                  {t("whiteLabel.Main Page Blocks")}
                </div>
                <div className="d-flex gap-2 mb-2">
                  <Form.Check
                    type="switch"
                    label={t("whiteLabel.New Events")}
                    checked={values.isNewEvents}
                    onChange={() =>
                      setFieldValue("isNewEvents", !values.isNewEvents)
                    }
                  />
                  <ImagePopover className="mt-1" img={NewEventsImg} />
                </div>

                <div className="d-flex gap-2 mb-2">
                  <Form.Check
                    type="switch"
                    label={t("whiteLabel.Upcoming Events")}
                    checked={values.isUpcomingEvents}
                    onChange={() =>
                      setFieldValue(
                        "isUpcomingEvents",
                        !values.isUpcomingEvents
                      )
                    }
                  />
                  <ImagePopover className="mt-1" img={UpcomingEventsImg} />
                </div>

                <div className="d-flex gap-2 mb-5">
                  <Form.Check
                    type="switch"
                    label={t("whiteLabel.Popular Performers")}
                    checked={values.isPopularPerformers}
                    onChange={() =>
                      setFieldValue(
                        "isPopularPerformers",
                        !values.isPopularPerformers
                      )
                    }
                  />
                  <ImagePopover className="mt-1" img={PopularPerformersImg} />
                </div>

                <Row className="mb-3">
                  <Col sm={4}>
                    <Button
                      id="save-project"
                      variant="success"
                      className="fw-bold w-100 py-2"
                      type="submit"
                      disabled={
                        isSubmitting ||
                        (submitCount > 0 && !isValid) ||
                        createOrUpdateWhiteLabelGeneralInformationLoading
                      }
                    >
                      {t("firstStep.save")}
                      {createOrUpdateWhiteLabelGeneralInformationLoading && (
                        <Spinner
                          animation="border"
                          role="status"
                          size="sm"
                          className="ms-2"
                        />
                      )}
                    </Button>
                  </Col>
                </Row>
              </div>
              <ApolloError
                networkError={
                  createOrUpdateWhiteLabelGeneralInformationError?.networkError
                }
              />
              <CustomToast
                isShow={isShowToast}
                handleClose={() => setIsShowToast(false)}
                message={t("whiteLabel.Changes Saved")}
                containerClassName={`${isMobile ? "py-3 px-5" : "p-5"}`}
              />
              <JoyrideHits />
            </FormikForm>
          );
        }}
      </Formik>
    </>
  );
}
